import React, { useEffect, useState, useCallback } from "react";
import { Link as RouterLink } from "react-router-dom";
import { getLeaderboard } from "../services/leaderboard";
import { User } from "../services/user";
import { LeaderboardData } from "../types";
import { SCHOOL_EMAIL_MAP, } from "../utils/emailToSchool";


import Box from "@mui/material/Box";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Snackbar from "@mui/material/Snackbar";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";

const CLASS_YEARS = [2025, 2026, 2027, 2028];
const RANKINGS_DISPLAY_LIMIT = 5;


interface RankedStudent extends User {
  rank: number;
}
interface ClassYearOption {
  label: string;
  value: string;
}
interface PositionOption {
  label: string;
  value: string;
}
interface SchoolOption {
  label: string;
  value: string;
}
const ROLE_LABELS: Record<string, string> = {
  engineer: "engineer",
  data_science: "quant",
  academia: "academia",
};
const classYearOptions: ClassYearOption[] = [
  { label: "all classes", value: "all" },
  ...CLASS_YEARS.map((year) => ({
    label: year.toString(),
    value: year.toString(),
  })),
];
const roleOptions: PositionOption[] = [
  { label: "all roles", value: "all" },
  { label: ROLE_LABELS.engineer, value: "engineer" },
  { label: ROLE_LABELS.data_science, value: "data_science" },
  { label: ROLE_LABELS.academia, value: "academia" },
];
const schoolOptions: SchoolOption[] = [
  { label: "all schools", value: "all" },
  ...Object.entries(SCHOOL_EMAIL_MAP)
    .map(([emailSuffix, schoolInfo]) => ({
      label: schoolInfo.schoolName,
      value: emailSuffix,
    }))
    .sort((a, b) => a.label.localeCompare(b.label)),
];

const RankDisplay: React.FC<{ rank: number; }> = ({ rank }) => {
  if (rank === 1) {
    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <EmojiEventsIcon
          sx={{ color: "#FFD700", fontSize: { xs: "1.1rem", sm: "1.4rem" } }}
        />
      </Box>
    );
  } else if (rank === 2) {
    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <EmojiEventsIcon
          sx={{ color: "#C0C0C0", fontSize: { xs: "1rem", sm: "1.3rem" } }}
        />
      </Box>
    );
  } else if (rank === 3) {
    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <EmojiEventsIcon
          sx={{ color: "#CD7F32", fontSize: { xs: "0.9rem", sm: "1.2rem" } }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ pl: 0.6, fontSize: { xs: "0.875rem", sm: "1rem" } }}>{rank}</Box>
  );
};

const Rankings: React.FC = () => {
  const [rankedStudents, setRankedStudents] = useState<RankedStudent[]>([]);
  const [totalStudentsCount, setTotalStudentsCount] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [copied, setCopied] = useState(false);
  const [selectedClassYear, setSelectedClassYear] =
    useState<ClassYearOption | null>(
      classYearOptions[0]
    );
  const [selectedRole, setSelectedRole] =
    useState<PositionOption | null>(
      roleOptions[0]
    );
  const [selectedSchool, setSelectedSchool] = useState<SchoolOption | null>(
    schoolOptions[0]
  );

  const loadRankings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: {
        class_year?: number | null;
        position?: string | null;
        limit?: number | null;
        school_email_suffix?: string | null;
      } = { limit: RANKINGS_DISPLAY_LIMIT };

      if (selectedClassYear && selectedClassYear.value !== "all") {
        filters.class_year = parseInt(selectedClassYear.value, 10);
      }
      if (selectedRole && selectedRole.value !== "all") {
        filters.position = selectedRole.value;
      }
      if (selectedSchool && selectedSchool.value !== "all") {
        filters.school_email_suffix = selectedSchool.value;
      }

      const data: LeaderboardData = await getLeaderboard(filters);
      const withRank = data.users.map((u, i) => ({ ...u, rank: i + 1 }));
      setRankedStudents(withRank);
      setTotalStudentsCount(data.total_count);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      setError(`Failed to load rankings: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, [selectedClassYear, selectedRole, selectedSchool]);

  useEffect(() => {
    loadRankings();
  }, [loadRankings]);


  const getInvitationMessage = () => {
    const classYearText =
      selectedClassYear?.value !== "all"
        ? `Class of ${selectedClassYear?.label}`
        : "any class year";
    const roleText =
      selectedRole?.value !== "all" ? selectedRole?.label : "any role";
    const schoolText =
      selectedSchool?.value !== "all" ? selectedSchool?.label : "any school";

    /* TODO: UPDATE THIS MESSAGE */
    return `Hey! We're looking for talented students on Ranked from ${schoolText} in ${classYearText} with experience as a ${roleText}.`;
  };


  const handleCopy = () => {
    navigator.clipboard
      .writeText(getInvitationMessage())
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      })
      .catch((err) => {
        console.error("Failed to copy text: ", err);
      });
  };


  const getFilterDescription = () => {
    const parts = [];

    if (selectedSchool?.value !== "all") {
      parts.push(selectedSchool?.label);
    }

    if (selectedClassYear?.value !== "all") {
      parts.push(`Class of ${selectedClassYear?.label}`);
    }

    if (selectedRole?.value !== "all") {
      parts.push(selectedRole?.label);
    }

    return parts.length > 0 ? parts.join(" • ") : "No filters applied";
  };

  const getRoleLabel = (dbValue: string) => ROLE_LABELS[dbValue] || dbValue;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        px: { xs: "1rem", sm: "1.5rem", md: "2rem" },
        marginX: "auto",
        bgcolor: "#f8f8f8",
        maxWidth: "100vw",
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 300,
            color: "#333333",
            letterSpacing: "0.025em",
            fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.875rem" },
            mb: { xs: 0.5, sm: 1 },
          }}
        >
          student leaderboard
        </Typography>
        <Typography
          variant="body2"
          sx={{
            mb: { xs: 0.75, sm: 1 },
            color: "rgba(51, 51, 51, 0.7)",
            fontWeight: 300,
            lineHeight: 1.5,
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
          }}
        >
          {totalStudentsCount > 0
            ? `showing top ${Math.min(
              RANKINGS_DISPLAY_LIMIT,
              totalStudentsCount
            )} of ${totalStudentsCount} students.`
            : `no students found matching your filters.`}
          <br />
          {totalStudentsCount > 0 && "based on elo ratings from peer votes"}
          {totalStudentsCount === 0 && `filters: ${getFilterDescription()}`}
        </Typography>
      </Box>

      <Box
        sx={{
          mb: { xs: 1, sm: 1.5 },
          mt: 1,
          flexShrink: 0,
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            flexWrap: "wrap",
            gap: { xs: 1, sm: 1.5 },
            alignItems: "stretch",
            width: "100%",
          }}
        >
          {/** Class Year */}
          <Box
            sx={{
              flexGrow: 1,
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <Autocomplete
              disablePortal
              id="class-year-filter"
              options={classYearOptions}
              value={selectedClassYear}
              onChange={(_, newValue) => setSelectedClassYear(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="class year"
                  size="small"
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontWeight: 300,
                      borderRadius: "4px",
                      "& fieldset": {
                        borderColor: "rgba(51, 51, 51, 0.2)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(51, 51, 51, 0.6)",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      fontWeight: 300,
                      color: "rgba(51, 51, 51, 0.7)",
                    },
                    "& .MuiInputBase-input": {
                      color: "#333333",
                      fontSize: { xs: "0.875rem", sm: "0.875rem" },
                    },
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              sx={{
                "& .MuiAutocomplete-endAdornment": {
                  "& .MuiButtonBase-root": {
                    color: "rgba(51, 51, 51, 0.5)",
                  },
                },
              }}
            />
          </Box>
          <Box
            sx={{
              flexGrow: 1,
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <Autocomplete
              disablePortal
              id="role-filter"
              options={roleOptions}
              value={selectedRole}
              onChange={(_, newValue) => setSelectedRole(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="role"
                  size="small"
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontWeight: 300,
                      borderRadius: "4px",
                      "& fieldset": {
                        borderColor: "rgba(51, 51, 51, 0.2)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(51, 51, 51, 0.6)",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      fontWeight: 300,
                      color: "rgba(51, 51, 51, 0.7)",
                    },
                    "& .MuiInputBase-input": {
                      color: "#333333",
                      fontSize: { xs: "0.875rem", sm: "0.875rem" },
                    },
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              sx={{
                "& .MuiAutocomplete-endAdornment": {
                  "& .MuiButtonBase-root": {
                    color: "rgba(51, 51, 51, 0.5)",
                  },
                },
              }}
            />
          </Box>

          <Box
            sx={{
              flexGrow: 1,
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <Autocomplete
              disablePortal
              id="school-filter"
              options={schoolOptions}
              value={selectedSchool}
              onChange={(_, newValue) => setSelectedSchool(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="school"
                  size="small"
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontWeight: 300,
                      borderRadius: "4px",
                      "& fieldset": {
                        borderColor: "rgba(51, 51, 51, 0.2)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(51, 51, 51, 0.6)",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      fontWeight: 300,
                      color: "rgba(51, 51, 51, 0.7)",
                    },
                    "& .MuiInputBase-input": {
                      color: "#333333",
                      fontSize: { xs: "0.875rem", sm: "0.875rem" },
                    },
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              sx={{
                "& .MuiAutocomplete-endAdornment": {
                  "& .MuiButtonBase-root": {
                    color: "rgba(51, 51, 51, 0.5)",
                  },
                },
              }}
            />
          </Box>
        </Box>
      </Box>

      {loading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 3,
            flexShrink: 0,
          }}
        >
          <CircularProgress sx={{ color: "rgba(51, 51, 51, 0.7)" }} />
        </Box>
      )}
      {error && (
        <Alert
          severity="error"
          sx={{
            my: 1,
            flexShrink: 0,
            fontWeight: 300,
            color: "#333333",
            borderRadius: "4px",
            "& .MuiAlert-icon": {
              color: "inherit",
            },
          }}
        >
          {error}
        </Alert>
      )}

      {/* No results state */}
      {!loading && !error && rankedStudents.length === 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: "center",
            my: 2,
            borderRadius: "4px",
            backgroundColor: "#ffffff",
            border: "1px solid rgba(51, 51, 51, 0.2)",
          }}
        >
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 300,
              color: "#333333",
              letterSpacing: "0.025em",
            }}
          >
            no students found for these filters
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mb: 3,
              fontWeight: 300,
              color: "rgba(51, 51, 51, 0.7)",
            }}
          >
            know someone who would be a great fit? invite them to join!
          </Typography>

          {/* Copyable invitation text field */}
          <Box sx={{ mb: 3, maxWidth: "600px", mx: "auto" }}>
            <TextField
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              value={getInvitationMessage()}
              InputProps={{
                readOnly: true,
                sx: {
                  fontWeight: 300,
                  color: "#333333",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(51, 51, 51, 0.2)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(51, 51, 51, 0.4)",
                  },
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      onClick={handleCopy}
                      aria-label="copy invitation text"
                      sx={{
                        mr: 0.5,
                        color: copied
                          ? "rgba(16, 185, 129, 0.8)"
                          : "rgba(51, 51, 51, 0.5)",
                      }}
                    >
                      {copied ? (
                        <CheckCircleOutlineIcon />
                      ) : (
                        <ContentCopyIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopy}
            sx={{
              mb: 2,
              color: copied ? "rgba(16, 185, 129, 0.8)" : "#333333",
              borderColor: copied
                ? "rgba(16, 185, 129, 0.5)"
                : "rgba(51, 51, 51, 0.2)",
              fontWeight: 300,
              fontSize: "0.875rem",
              textTransform: "none",
              padding: "0.5rem 1.5rem",
              borderRadius: "4px",
              "&:hover": {
                borderColor: copied
                  ? "rgba(16, 185, 129, 0.8)"
                  : "rgba(51, 51, 51, 0.6)",
                backgroundColor: "transparent",
              },
            }}
          >
            {copied ? "copied!" : "copy invitation"}
          </Button>

          {/* Snackbar to confirm copy */}
          <Snackbar
            open={copied}
            autoHideDuration={3000}
            onClose={() => setCopied(false)}
            message="invitation copied to clipboard!"
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            sx={{
              "& .MuiSnackbarContent-root": {
                backgroundColor: "rgba(51, 51, 51, 0.9)",
                fontWeight: 300,
                borderRadius: "4px",
              },
            }}
          />
        </Paper>
      )}

      {/* Table with updated column order and fixed scrolling */}
      {!loading && !error && rankedStudents.length > 0 && (
        <Box
          sx={{
            flexGrow: 1,
            overflow: "auto",
            mt: 1,
            backgroundColor: "#ffffff",
            border: "1px solid rgba(51, 51, 51, 0.2)",
            borderRadius: "4px",
          }}
        >
          <TableContainer
            className="hide-scrollbar"
            sx={{
              maxHeight: "calc(100vh - 250px)",
              overflowY: "auto",
              overflowX: "auto",
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 400,
                      color: "#333333",
                      borderBottom: "1px solid rgba(51, 51, 51, 0.2)",
                      backgroundColor: "rgba(251, 251, 251, 0.9)",
                      padding: { xs: "8px 6px", sm: "16px" },
                      width: { xs: "40px", sm: "60px" },
                    }}
                  >
                    rank
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 400,
                      color: "#333333",
                      borderBottom: "1px solid rgba(51, 51, 51, 0.2)",
                      backgroundColor: "rgba(251, 251, 251, 0.9)",
                      padding: { xs: "8px 6px", sm: "16px" },
                      minWidth: { xs: "80px", sm: "120px" },
                    }}
                  >
                    name
                  </TableCell>
                  {/* Move Class next to Name */}
                  <TableCell
                    sx={{
                      fontWeight: 400,
                      color: "#333333",
                      borderBottom: "1px solid rgba(51, 51, 51, 0.2)",
                      backgroundColor: "rgba(251, 251, 251, 0.9)",
                      padding: { xs: "8px 6px", sm: "16px" },
                      width: { xs: "50px", sm: "80px" },
                    }}
                  >
                    class
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 400,
                      color: "#333333",
                      borderBottom: "1px solid rgba(51, 51, 51, 0.2)",
                      backgroundColor: "rgba(251, 251, 251, 0.9)",
                      padding: { xs: "8px 6px", sm: "16px" },
                      minWidth: { xs: "80px", sm: "120px" },
                    }}
                  >
                    role
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 400,
                      color: "#333333",
                      borderBottom: "1px solid rgba(51, 51, 51, 0.2)",
                      backgroundColor: "rgba(251, 251, 251, 0.9)",
                      padding: { xs: "8px 6px", sm: "16px" },
                      width: { xs: "50px", sm: "80px" },
                      textAlign: "right",
                    }}
                  >
                    elo
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rankedStudents.map((s) => (
                  <TableRow
                    key={s.id}
                    sx={{
                      "& td": {
                        fontWeight: 300,
                        color: "#333333",
                        borderBottom: "1px solid rgba(51, 51, 51, 0.1)",
                        padding: { xs: "8px 6px", sm: "16px" },
                      },
                      "&:last-child td, &:last-child th": {
                        border: 0,
                      },
                      "&:hover": {
                        backgroundColor: "rgba(51, 51, 51, 0.03)",
                      },
                    }}
                  >
                    <TableCell>
                      <RankDisplay rank={s.rank} />
                    </TableCell>
                    <TableCell>
                      <Link
                        component={RouterLink}
                        to={`/users/${s.id}`}
                        underline="hover"
                        sx={{
                          color: "#333333",
                          fontWeight: 300,
                          textDecoration: "none",
                          transition: "color 0.2s",
                          "&:hover": {
                            color: "rgba(51, 51, 51, 0.7)",
                          },
                          fontSize: { xs: "0.875rem", sm: "1rem" },
                          whiteSpace: "nowrap",
                        }}
                      >
                        {s.name}
                      </Link>
                    </TableCell>
                    {/* Class moved here */}
                    <TableCell>
                      {s.class_year ? (
                        <Link
                          component="button"
                          underline="hover"
                          sx={{
                            cursor: "pointer",
                            fontWeight: 300,
                            color: "#333333",
                            textDecoration: "none",
                            background: "none",
                            border: "none",
                            padding: 0,
                            transition: "color 0.2s",
                            "&:hover": {
                              color: "rgba(51, 51, 51, 0.7)",
                            },
                            fontSize: { xs: "0.875rem", sm: "1rem" },
                          }}
                          onClick={() => {
                            const year = s.class_year!.toString();
                            const yearOption =
                              classYearOptions.find(
                                (opt) => opt.value === year
                              ) || null;
                            setSelectedClassYear(yearOption);
                          }}
                        >
                          {s.class_year}
                        </Link>
                      ) : (
                        "N/A"
                      )}
                    </TableCell>
                    {/* Position renamed to Role */}
                    <TableCell
                      sx={{
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                        whiteSpace: "nowrap",
                      }}
                    >
                      {s.position ? getRoleLabel(s.position) : "N/A"}
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "right",
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                      }}
                    >
                      {s.elo}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Add a style block to hide scrollbars on mobile */}
      <style>{`
        @media (max-width: 600px) {
          .hide-scrollbar::-webkit-scrollbar { display: none; }
          .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        }
      `}</style>
    </Box>
  );
};

export default Rankings;
